import { useState } from 'react';
import type { CsvColumn } from '@/shared/lib';
import { Button } from './Button';
import { ExportDialog } from './ExportDialog';

interface ExportButtonProps<T> {
  /** What the table holds, e.g. its card title; it names the file and the button for readers. */
  name: string;
  columns: readonly CsvColumn<T>[];
  /**
   * The rows in the table's order, or a loader for a table that shows one page of a larger set
   * (the loader fetches every row, or as many as `limit` allows).
   */
  rows: readonly T[] | (() => Promise<readonly T[]>);
  /** Rows the table's source holds, for a loader that may fetch fewer (`limit`). */
  total?: number | undefined;
  limit?: number | undefined;
  /** Nothing to export yet (loading, empty); a plain empty array disables the button by itself. */
  disabled?: boolean;
  id?: string;
}

/**
 * `button.btn--link` with a download icon, in a card header or next to a table: opens the export
 * dialog (format, columns, filters, grouping, record count), which saves the table as a file
 * named after it and the day, e.g. `mrr-bridge-2026-10-15.csv`. Columns and rows come from the
 * table's data, so the values are raw figures in the table's current order.
 */
export function ExportButton<T>({
  name,
  columns,
  rows,
  total,
  limit,
  disabled = false,
  id,
}: ExportButtonProps<T>) {
  const [open, setOpen] = useState(false);
  const empty = typeof rows !== 'function' && rows.length === 0;
  const load = async () => {
    const list = typeof rows === 'function' ? await rows() : rows;
    return list.map((row) => columns.map((column) => column.value(row)));
  };

  return (
    <>
      <Button
        variant="link"
        size="small"
        icon="download"
        id={id}
        aria-label={`Export: ${name}`}
        aria-haspopup="dialog"
        title="Save this table as CSV, Excel or PDF"
        disabled={disabled || empty}
        onClick={() => {
          setOpen(true);
        }}
      >
        Export
      </Button>
      <ExportDialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        name={name}
        headers={columns.map((column) => column.label)}
        load={load}
        total={total}
        limit={limit}
      />
    </>
  );
}
